import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ArrowLeft from '@deliveroo/icon-set/react/ArrowLeft';
import ArrowRight from '@deliveroo/icon-set/react/ArrowRight';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import styles from './styles.scss';

// eslint-disable-next-line react/no-deprecated
class Carousel extends React.Component {
  static propTypes = {
    children: PropTypes.arrayOf(PropTypes.node).isRequired,
    id: PropTypes.string.isRequired,
    bounceIn: PropTypes.bool,
    centerActiveItem: PropTypes.bool,
    controlOffsetY: PropTypes.number,
    /**
     * Offsets for control buttons. Index 0 controls back button offset. Index 1 controls forward button offset.
     * [backButtonOffset, forwardButtonOffset]
     */
    controlOffsetX: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /**
     * The number of children to slide forward or backward
     */
    slideBy: PropTypes.number,
  };

  static defaultProps = {
    bounceIn: false,
    centerActiveItem: false,
    controlOffsetY: 0,
    controlOffsetX: [0, 0],
    slideBy: 1,
  };

  constructor(props) {
    super(props);

    this.state = {
      currentListPosition: 0,
      activeItem: 0,
      bounceIn: false,
      isMobileView: false,
      windowHasResized: false,
      obscuredSlides: [],
      childNodesMounted: false,
    };

    this.childNodes = [];
    this.reregisterItems = 0;
    this.listPositions = [0];

    this.registerChildNodeDetails = this.registerChildNodeDetails.bind(this);
    this.calculateListPositionProperties = this.calculateListPositionProperties.bind(this);
    this.handleOnWindowResize = this.handleOnWindowResize.bind(this);
    this.runEntryAnimation = this.runEntryAnimation.bind(this);
    this.initializeCarousel = this.initializeCarousel.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleOnWindowResize);
    this.initializeCarousel();
  }

  componentWillReceiveProps(newProps) {
    if (this.listDifferent(this.props.children, newProps.children)) {
      this.childNodes = [];
      this.listPositions = [0];
      this.setState({
        activeItem: 0,
        currentListPosition: 0,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.listDifferent(prevProps.children, this.props.children)) {
      this.handleSlidesUpdated();
    }

    if (this.state.childNodesMounted) {
      this.fadeObscuredSlides();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ childNodesMounted: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleOnWindowResize);
  }

  initializeCarousel() {
    this.childNodes = [];
    this.handleSlidesUpdated();
    this.fadeObscuredSlides();
  }

  handleSlidesUpdated() {
    this.runEntryAnimation();
    this.carouselWindowNode.scrollLeft = 0;
    this.reregisterItems += 1;
  }

  runEntryAnimation() {
    if (this.props.bounceIn) {
      this.setState({ bounceIn: true });
      setTimeout(() => {
        this.setState({ bounceIn: false });
      }, 1000);
    }
  }

  handleOnWindowResize() {
    if (window.innerWidth < 768) {
      this.setState({
        isMobileView: true,
        windowHasResized: true,
      });
    } else {
      this.setState({
        isMobileView: false,
        windowHasResized: true,
      });
      this.carouselWindowNode.scrollLeft = 0;
      this.reregisterItems += 1;
    }
  }

  listDifferent(oldChildren, newChildren) {
    const diffArr = oldChildren.filter((oldChild) =>
      newChildren.find((newChild) => oldChild.props.id === newChild.props.id),
    );
    return oldChildren.length !== newChildren.length || oldChildren.length !== diffArr.length;
  }

  moveCarousel(direction) {
    if (this.state.windowHasResized) {
      this.listPositions = [0];
      this.setState({ windowHasResized: false });
    }

    if (this.listPositions.length === 1) {
      this.calculateListPositionProperties();
    }

    const newActiveItemIndex = this.calculateNextActiveItemIndex(direction);

    this.setState({
      activeItem: newActiveItemIndex,
      currentListPosition: this.listPositions[newActiveItemIndex],
    });

    this.fadeObscuredSlides(newActiveItemIndex);
    this.childNodes = [];
  }

  calculateNextActiveItemIndex(direction) {
    const activeItem = this.state.activeItem;
    const childNodes = this.childNodes;

    // this assumes all slides are the same width. if that's not the case, this will need to change
    const slideWidth = childNodes[0].node.offsetWidth || 0;
    const maxSlideBy = Math.floor(this.carouselNode.clientWidth / slideWidth);
    const slideByProp = this.props.slideBy;
    const slideBy = slideByProp > maxSlideBy ? maxSlideBy : slideByProp;

    // we don't want to decrement or increment past the first slide or the last slide
    if (direction === 'prev') {
      const newActiveItemIndex = activeItem - slideBy;
      return newActiveItemIndex < 0 ? 0 : newActiveItemIndex;
    }
    const newActiveItemIndex = activeItem + slideBy;
    return newActiveItemIndex > childNodes.length - 1 ? childNodes.length - 1 : newActiveItemIndex;
  }

  registerChildNodeDetails(nodeDetails) {
    this.childNodes = this.childNodes.filter((childNode) => childNode.id !== nodeDetails.id);
    this.childNodes.push(nodeDetails);
    if (this.childNodes.length === this.props.children.length) {
      this.setState({ childNodesMounted: true });
    }
  }

  fadeObscuredSlides(activeItem = this.state.activeItem) {
    const el = this.carouselNode;
    const obscuredSlides = [];
    this.childNodes.forEach((child) => {
      const slideOffset = child.node.offsetLeft + this.listPositions[activeItem];
      const childWidth = child.node.clientWidth;
      if (slideOffset < 0 || slideOffset + childWidth > el.clientWidth) {
        obscuredSlides.push(child.position);
      }
    });

    this.setState({ obscuredSlides });
  }

  calculateListPositionProperties() {
    const listDimensions = this.carouselNode.getBoundingClientRect();
    this.listPositions = this.childNodes.map((nodeDetails) => {
      if (!nodeDetails.position) return 0;
      const item = this.returnChildNode(nodeDetails.position);
      const itemDimensions = item.node.getBoundingClientRect();
      return this.calculateNodeOffsetAmount(itemDimensions, listDimensions);
    });
  }

  calculateNodeOffsetAmount(itemDimensions, listDimensions) {
    const itemOffset = this.props.centerActiveItem
      ? (listDimensions.width - itemDimensions.width) / 2 - 8
      : 0;

    return (
      this.state.currentListPosition - (itemDimensions.left - listDimensions.left) + itemOffset
    );
  }

  returnChildNode(position) {
    return this.childNodes.find((node) => position === node.position);
  }

  render() {
    const { activeItem, obscuredSlides, isMobileView, currentListPosition, bounceIn } = this.state;

    const childrenWithCarouselProps = React.Children.map(this.props.children, (child, i) => {
      const identifier = `${this.props.id}-${i}`;
      return React.cloneElement(child, {
        key: identifier,
        id: identifier,
        position: i,
        isActive: i === activeItem,
        registerNodeDetails: this.registerChildNodeDetails,
        reregisterItems: this.reregisterItems,
        isObscured: obscuredSlides.includes(i),
        activeItem,
      });
    });

    const listPosition = isMobileView ? 0 : currentListPosition;
    const lastSlideVisible = !obscuredSlides.includes(this.childNodes.length - 1);

    const carouselListClasses = classNames(styles['carousel-list'], {
      [styles['bounce-in']]: bounceIn,
    });

    const prevControlClassNames = classNames(styles['carousel-control'], {
      [styles.hide]: !activeItem || bounceIn,
    });

    const nextControlClassNames = classNames(styles['carousel-control'], {
      [styles.hide]: bounceIn || lastSlideVisible,
    });

    const ctrlOffsetY = `${50 + this.props.controlOffsetY}%`;
    const backCtrlPosition = {
      top: ctrlOffsetY,
      left: this.props.controlOffsetX[0] || 0,
      right: 0,
    };
    const forwardCtrlPosition = {
      top: ctrlOffsetY,
      left: 'initial',
      right: this.props.controlOffsetX[1] || 0,
    };

    return (
      <div
        className={styles.carousel}
        ref={(el) => {
          this.carouselNode = el;
        }}
      >
        <React.Fragment>
          <button
            tabIndex="-1"
            type="button"
            style={backCtrlPosition}
            className={prevControlClassNames}
            onClick={() => this.moveCarousel('prev')}
          >
            <Icon component={ArrowLeft} color="teal" size={24} />
          </button>
          <div
            className={styles['carousel-window']}
            ref={(el) => {
              this.carouselWindowNode = el;
            }}
          >
            <ul
              className={carouselListClasses}
              style={{ transform: `translateX(${listPosition}px)` }}
            >
              {childrenWithCarouselProps}
            </ul>
          </div>
          <button
            tabIndex="-1"
            type="button"
            style={forwardCtrlPosition}
            className={nextControlClassNames}
            onClick={() => this.moveCarousel()}
          >
            <Icon component={ArrowRight} color="teal" size={24} />
          </button>
        </React.Fragment>
      </div>
    );
  }
}

export default Carousel;
